import { useQuery } from '@tanstack/react-query';
import { Link, Outlet } from 'react-router-dom';
import { AvisoDeVencimiento, cerrarSesion, releerIdentidad, useSesion } from '../sesion';
import type { Sesion } from '../sesion';

/**
 * Marco común de las pantallas con sesión: barra con quién soy, navegación y
 * salida.
 *
 * Cuelga de una {@link RutaProtegida}, así que acá siempre hay sesión; si no la
 * hubiera, esto no se habría montado.
 */
export function Layout() {
  const { sesion } = useSesion();
  if (!sesion) {
    return null; // Inalcanzable: lo garantiza RutaProtegida. Es para el tipo.
  }
  return (
    <>
      <BarraDeSesion sesion={sesion} />
      <AvisoDeVencimiento sesion={sesion} />
      <Outlet />
    </>
  );
}

function BarraDeSesion({ sesion }: { sesion: Sesion }) {
  // Relee quién soy contra el backend. Sirve para dos cosas: enterarse de un
  // cambio de rol hecho por otra persona (la autorización sale de la base, no
  // de los claims del token) y, si la cuenta fue desactivada, comerse el 401
  // que cierra la sesión. TanStack Query contiene el error: no hay `throw` que
  // se escape al render.
  useQuery({
    queryKey: ['yo', sesion.usuarioId],
    queryFn: releerIdentidad,
    staleTime: 60_000,
  });

  return (
    <header className="barra">
      <nav>
        <Link to="/">Inicio</Link>
        {/* Sin condición de rol: ADMIN y OPERADOR son los únicos dos roles y
            los dos pueden emitir (ver App.tsx). */}
        <Link to="/emision">Emitir</Link>
        <Link to="/comprobantes">Comprobantes</Link>
        <Link to="/tenants">Tenants</Link>
        <Link to="/contrato">Contrato de la API</Link>
        {/* Esconder el link NO es control de acceso: el backend exige
            ROLE_ADMIN igual, y si alguien escribe /admin a mano se lleva la
            pantalla de "no tenés permiso". Esto es para no ofrecer puertas
            cerradas. */}
        {sesion.rol === 'ADMIN' ? <Link to="/admin">Administración</Link> : null}
      </nav>
      <span>
        {sesion.nombre} ({sesion.rol})
      </span>
      <button type="button" onClick={cerrarSesion}>
        Salir
      </button>
    </header>
  );
}
